import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from 'react-router-dom';
import { CreditCard } from 'lucide-react';
import { API_CONFIG, getAuthHeadersWithoutCredentials } from '../constants/api';

export const CreditsPage = () => {
    const { isAuthenticated, isLoading } = useAuth0();
    const navigate = useNavigate();
    const [selected, setSelected] = useState(null);
    const [isRedirecting, setIsRedirecting] = useState(false);

    useEffect(() => {
        if (!isAuthenticated && !isLoading) {
            navigate('/');
            return;
        }
    }, [isAuthenticated, isLoading, navigate]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin"></div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return null;
    }

    const creditOptions = [
        { amount: 50, price: 5 },
        { amount: 100, price: 10 },
        { amount: 500, price: 50 },
        { amount: 1000, price: 100 },
    ];

    const selectedOption = creditOptions.find((option) => option.amount === selected);

    const handlePurchase = async () => {
        if (!selectedOption) return;
        try {
            setIsRedirecting(true);
            const optionMap: Record<number, number> = { 50: 1, 100: 2, 500: 3, 1000: 4 };
            const optionCode = optionMap[selectedOption.amount];
            const res = await fetch(
                `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PAYMENT_LINK}?option=${optionCode}`,
                { headers: getAuthHeadersWithoutCredentials() }
            );
            if (!res.ok) throw new Error('Failed to get payment link');

            if (res.redirected && res.url) {
                window.open(res.url, '_blank', 'noopener,noreferrer');
                return;
            }

            const contentType = res.headers.get('content-type') || '';
            let url: string | undefined;
            if (contentType.includes('application/json')) {
                const data = await res.json();
                url = typeof data === 'string'
                    ? data
                    : (data?.url || data?.link || data?.payment_link || data?.paymentUrl || data?.payment_url);
            } else {
                const text = await res.text();
                if (text && /^https?:\/\//i.test(text.trim())) {
                    url = text.trim();
                }
            }

            if (!url) {
                const locationHeader = res.headers.get('location');
                if (locationHeader && /^https?:\/\//i.test(locationHeader)) {
                    url = locationHeader;
                }
            }

            if (url) {
                window.open(url, '_blank', 'noopener,noreferrer');
                return;
            }

            setIsRedirecting(false);
        } catch (err) {
            setIsRedirecting(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-background px-4 py-10">
            <div className="max-w-3xl w-full space-y-6">
                <div className="text-center">
                    <h1 className="text-2xl font-semibold text-card-foreground mb-1">Buy Credits</h1>
                    {/* <p className="text-sm text-muted-foreground">Power your AI interactions with credits</p> */ }
                </div>

                {/* Pro Subscription Box */ }
                <div className="border rounded-xl p-4 mt-8 flex items-center justify-between border-border bg-card">
                    <div>
                        <p className="font-semibold text-card-foreground">✨ Pro Subscription</p>
                        <p className="text-muted-foreground text-sm">For professional use</p>
                        <button className="mt-2 btn-primary text-sm">
                            Coming soon!
                        </button>
                    </div>
                    <div className="text-muted-foreground text-sm">⚡ Unlimited regenerations on all projects</div>
                </div>

                {/* Credit Options */ }
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
                    { creditOptions.map((option) => (
                        <div
                            key={ option.amount }
                            onClick={ () => setSelected(option.amount) }
                            className={ `border rounded-xl p-4 cursor-pointer flex justify-between items-center hover:shadow transition bg-card ${selected === option.amount ? "border-primary border-2" : "border-border"
                                }` }
                        >
                            <div>
                                <p className="font-medium text-card-foreground">{ option.amount } Credits</p>
                                <p className="text-muted-foreground text-sm">${ option.price.toFixed(2) }</p>
                            </div>
                            <CreditCard className="text-muted-foreground" />
                        </div>
                    )) }
                </div>

                {/* Purchase Button */ }
                <div className="mt-6 flex items-center justify-center">
                    <button
                        onClick={ handlePurchase }
                        disabled={ !selectedOption || isRedirecting }
                        className={ `px-6 py-2 text-sm rounded-md font-medium transition-colors ${selectedOption
                            ? "btn-primary"
                            : "bg-muted text-muted-foreground cursor-not-allowed"
                            }` }
                    >
                        { isRedirecting
                            ? 'Redirecting...'
                            : selectedOption
                                ? `Purchase ${selectedOption.amount} Credits for $${selectedOption.price.toFixed(2)}`
                                : "Select an amount to purchase" }
                    </button>
                </div>
            </div>
        </div>
    );
};
